import {useNavigate} from 'react-router-dom';
import {useToggle} from '@mantine/hooks';
import {
    AiOutlineEdit,
    AiOutlineProfile
} from 'react-icons/ai';
import {IconContext} from 'react-icons';
import {
    FlexiButton,
    FlexiCancelButton
} from "@flexinet/ui-components";
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {EditButton} from "../buttons/EditButton.tsx";
import {DecommissionButton} from "../buttons/DecommissionButton.tsx";
import {MarkDecommissionedButton} from "../buttons/MarkDecommissionedButton.tsx";
import {MarkApplicationDecommissionedResult} from "../services/ApplicationsService.ts";
import {Application} from "../types.ts";
import {ApplicationStatus} from "../types.ts";
import {ApplicationInfrastructure} from "../types-infrastructure.ts";
import {isTeardownSubject} from "../utils/isTeardownSubject.ts";

export const useEditPageTools = (
    application?: Application,
    stage?: 'dev' | 'test' | 'prod',
    onDecommission?: () => void,
    onMarkDecommissioned?: () => Promise<MarkApplicationDecommissionedResult | undefined>,
    // FNP-1013: open the provision dialog. Optional, so a caller that does not
    // offer provisioning simply omits it and no button renders.
    onProvision?: () => void,
    // ⚠️ FNP-1601: the CURRENT (non-terminal) infrastructure record, which is
    // what the server's teardown reads. Passed down to the Decommission button
    // so the blast radius it shows is derived from the same record the server
    // will act on. Undefined means "no inventory row" and the legacy
    // `Application` fields are read instead.
    infrastructure?: ApplicationInfrastructure | null,
) => {

    const navigate = useNavigate();

    const {allowedActions} = useVerifiedPermissions()

    const [mode, toggle] = useToggle(['view', 'edit']);

    const tools = [];


    if (allowedActions.includes('UpdateApplication')) {

        if (mode === 'edit') {
            tools.push(<FlexiButton variant={'filled'} key={'profile'}
                                    leftSection={<IconContext.Provider value={{size: '24px'}}>
                                        <AiOutlineProfile/></IconContext.Provider>}
                                    onClick={() => toggle()}>Profile</FlexiButton>);
        } else {
            tools.push(<EditButton leftSection={<AiOutlineEdit/>} variant={'filled'}
                                   key={'edit'}>Edit</EditButton>);
        }

    }

    // FNP-998: Decommission lives in the page-header toolbar, like every other
    // action on this platform. An earlier version put it at the bottom of the
    // page body on the theory that a destructive action should sit away from
    // routine ones — that was wrong. It broke the convention, and a button
    // nobody can find is worse than one beside Edit. The protection against a
    // misclick is the typed-name confirmation, not obscurity.
    //
    // Shown only for programmatically provisioned applications: an
    // admin-created record has no infrastructure to tear down, so offering the
    // action would be misleading.
    //
    // ═══ FNP-1234: ASK THE STACK NAME, NOT `businessDomain` ═══
    //
    // This used to read `application.businessDomain` as its "was this
    // provisioned?" signal. That held only while `createApplication` created
    // AND provisioned in one mutation. FNP-1013 split them, and the field went
    // with the split — it is a provisioning INPUT on `provisionApplication`,
    // not a property of the record, so an application built through the new
    // mutation owns a stack, a bucket, two repos and two pipelines while its
    // record carries no `businessDomain` at all.
    //
    // Every such application had NO Decommission button. Not a disabled one, a
    // missing one — no route to teardown through the product.
    //
    // That fix moved the gate to `infraStackNames`, which the orchestrator
    // writes and the SERVER gate reads, so the UI and the API agreed about
    // what counts as a teardown candidate.
    //
    // ⚠️ FNP-1027: the gate above described this behaviour and did not deliver
    // it. `hasProvisionedInfrastructure` answers "does this record OWN
    // infrastructure", and returns false when `infraStackNames` is null — so a
    // never-provisioned record never mounted the button, which is exactly the
    // case FNP-1027 exists to handle. Verified in the browser on `river`
    // (CREATING, every repo/pipeline id null): the header offered no
    // Decommission button at all, so the refusal could not be reached by
    // clicking.
    //
    // The mount now asks the question the comment above actually describes —
    // "is this a plausible teardown subject" — and the per-stage refusal stays
    // in `getDecommissionEligibility`, which names the right remedy for each
    // shape. See isTeardownSubject.ts for why this is a separate predicate
    // rather than a widening of the other one.
    if (mode === 'view'
        && application?.id
        && isTeardownSubject(application)
        && allowedActions.includes('DeleteApplication')) {
        tools.push(
            <DecommissionButton
                key={'decommission'}
                record={application}
                stage={stage ?? 'dev'}
                onDecommission={() => onDecommission?.()}
                infrastructure={infrastructure}
            />,
        );
    }

    // FNP-1013: Provision — build the infrastructure, choosing the playbook.
    //
    // Until this existed there was no way to provision from the console at all.
    // Creating a record and building its infrastructure were one mutation, and
    // whether to build was inferred from whether `businessDomain` happened to
    // be populated — so filling in a dropdown silently created two repos, two
    // pipelines, a bucket, a distribution and DNS.
    //
    // ⚠️ Gated on `onProvision` being supplied and on UpdateApplication rather
    // than on `businessDomain`. Gating on that field is exactly the inference
    // this ticket removes: it stands in for "was this provisioned?", which
    // nothing actually records. An application with no infrastructure is
    // precisely the one that needs this button.
    //
    // ⚠️ Deliberately NOT hidden once an application has been provisioned.
    // Re-provisioning is a real need (a failed run leaves a half-built app),
    // and the guard against double-provisioning belongs in the state machine's
    // first step, not in whether a button renders — see the note on
    // ValidateProvisionable. Hiding the button would make a stuck application
    // unrecoverable from the console.
    if (mode === 'view'
        && application?.id
        && onProvision
        && allowedActions.includes('UpdateApplication')) {
        tools.push(
            <FlexiButton
                key={'provision'}
                variant={'default'}
                onClick={() => onProvision()}
            >
                Provision
            </FlexiButton>,
        );
    }

    // FNP-1007: "Mark as decommissioned" — correct a record whose infrastructure
    // is already gone. Sits BESIDE Decommission rather than being hidden inside
    // its refusal dialog, for two reasons:
    //
    //   1. The refusal dialog is not the only way to arrive here. An admin
    //      auditing the six orphaned ACTIVE records knows before clicking
    //      anything that there is nothing to tear down; routing them through a
    //      destructive action's refusal to reach a non-destructive one is a
    //      worse path than offering it directly.
    //   2. Discoverability is the platform convention (the FNP-998 note below
    //      records that hiding Decommission was already tried and was wrong).
    //      An action reachable only as the consolation prize of another
    //      action's failure is effectively undiscoverable.
    //
    // The refusal dialog ALSO points here, so both routes work — but this is the
    // affordance, not a hidden escalation.
    //
    // ⚠️ Shown regardless of whether a stack name is recorded. The whole point
    // is the case where there is nothing to record, so gating it on the same
    // condition Decommission gates on would reproduce the dead end exactly.
    // The server sweeps and refuses on its own; the UI must not pre-judge.
    //
    // Hidden once the record is already DECOMMISSIONED — there is nothing left
    // to correct.
    //
    // ⚠️ FNP-1007: GATED ON `DeleteApplication`, NOT on its own id, and that is
    // a deliberate interim choice by the owner (2026-08-19).
    //
    // `rbacActions.MarkApplicationDecommissioned` exists in the enum below and
    // is the semantically correct gate — this action's blast radius is the
    // OPPOSITE of DeleteApplication's, destroying nothing and only correcting a
    // record. But `useVerifiedPermissions` resolves action ids dynamically
    // against the Cedar policy store, so an id the store does not know renders
    // the button for NOBODY. Adding the enum member is necessary and not
    // sufficient; the policy store is admin-console/AVP work outside this repo.
    //
    // `DeleteApplication` is the closest existing id that already resolves: both
    // are admin-only and both move a record to a terminal state. Reusing it
    // makes the affordance reachable today rather than after a policy-store
    // change.
    //
    // ⚠️ SWITCH THIS BACK once `MarkApplicationDecommissioned` is in the policy
    // store — see FNP-1007. Leaving it on `DeleteApplication` means an admin who
    // may correct records but must never destroy infrastructure cannot be
    // granted the safe action without also being granted the destructive one.
    // ⚠️ FNP-1234: NO `businessDomain` CONDITION, and its removal is the point.
    //
    // This gate carried one until 2026-08-26, which contradicted the paragraph
    // directly above it: a record that has never been near the orchestrator is
    // precisely the orphan this button exists to correct, and `businessDomain`
    // reports nothing about infrastructure either way. It was the same stale
    // inference the Decommission gate above carried, in the place where it did
    // the most damage — the repair path was missing on exactly the records
    // needing repair, including the ones the refusal dialog now points here.
    //
    // Nothing replaces it: "must not pre-judge" means no infrastructure test at
    // all. The server sweeps four resource types and refuses on its own.
    if (mode === 'view'
        && application?.id
        && application.applicationStatus !== ApplicationStatus.DECOMMISSIONED
        && allowedActions.includes('DeleteApplication')) {
        tools.push(
            <MarkDecommissionedButton
                key={'mark-decommissioned'}
                record={application}
                onMarkDecommissioned={async () => await onMarkDecommissioned?.()}
            />,
        );
    }

    tools.push(<FlexiCancelButton key={'close'}
                                  onClick={() => navigate(`/applications`)}/>);


    return {
        mode,
        tools,
    };

};
